<main class="container layout-39">
  <div id="containerLeftMenu" style="margin-right: 15px">
    <left-menu />
  </div>
  <section id="column2">
    <div class="row">
      <div class="col col-12">

        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;">
          <h1>Assessment Job #{{ jobId }}</h1>
          <button type="button" class="btn btn-secondary no-arrow" (click)="goBack()">
            ← Back to Code Assessment History
          </button>
        </div>

        @if (error) {
          <div class="alert alert-danger">
            <mat-icon>error_outline</mat-icon> {{ error }}
          </div>
        }

        @if (loading) {
          <app-spinner></app-spinner>
        }

        @if (!loading && job) {
          @if (isRateLimited(job)) {
            <div class="alert alert-warning" role="status">
              <mat-icon>schedule</mat-icon>
              <span>
                GitHub API rate limit reached. This assessment is paused until
                <strong>{{ formatRateLimitedUntil(job) }} UTC</strong> and will take longer than usual.
                It will resume automatically.
              </span>
            </div>
          }

          <!-- Job Summary -->
          <mat-card style="margin-bottom: 20px;">
            <mat-card-header>
              <mat-card-title>Job Summary</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">Status</span>
                  <span class="badge {{ getStatusClass(job.status) }}">{{ getStatusLabel(job.status) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Type</span>
                  <span>{{ getJobTypeLabel(job) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Repository</span>
                  <a [href]="job.repositoryUrl" target="_blank" rel="noopener">{{ job.repositoryUrl }}</a>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Branch</span>
                  <span>{{ job.branch }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Orion ID</span>
                  <span>{{ job.orionId || '—' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Requested By</span>
                  <span>{{ job.requestedByEmail || '—' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Total Score</span>
                  <strong>{{ isLightweightJob(job) ? '—' : (job.totalScore ?? '—') }}</strong>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Created</span>
                  <span>{{ formatDate(job.createdAt) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Started</span>
                  <span>{{ formatDate(job.startedAt) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Completed</span>
                  <span>{{ formatDate(job.completedAt) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Duration</span>
                  <span>{{ getDuration(job) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Tokens (prompt / completion / total)</span>
                  <span>{{ job.totalPromptTokens }} / {{ job.totalCompletionTokens }} / {{ job.totalTokens }}</span>
                </div>
                @if (job.errorMessage) {
                  <div class="detail-item detail-full">
                    <span class="detail-label">Error</span>
                    <span class="error-message">{{ job.errorMessage }}</span>
                  </div>
                }
              </div>
            </mat-card-content>
          </mat-card>

          @if (isLightweightJob(job) && job.lightweightResult) {
          <mat-card style="margin-bottom: 20px;">
            <mat-card-header>
              <mat-card-title>Lightweight Code Validation Result</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">CMDB Comparison</span>
                  <strong>{{ job.lightweightResult.cmdbComparison }}</strong>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Pipeline Maturity</span>
                  <span>{{ job.lightweightResult.pipelineMaturity }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Files / Metadata Files</span>
                  <span>{{ job.lightweightResult.codebaseSize.totalFiles }} / {{ job.lightweightResult.codebaseSize.manifestFiles }}</span>
                </div>
                <div class="detail-item detail-full">
                  <span class="detail-label">Message</span>
                  <span>{{ job.lightweightResult.message || '—' }}</span>
                </div>
                <div class="detail-item detail-full">
                  <span class="detail-label">Recommended Action</span>
                  <span>{{ job.lightweightResult.recommendedAction }}</span>
                </div>
                <div class="detail-item detail-full">
                  <span class="detail-label">Technology Stack</span>
                  <span>{{ job.lightweightResult.technologyStack.join(', ') || '—' }}</span>
                </div>
                <div class="detail-item detail-full">
                  <span class="detail-label">Legacy Indicators</span>
                  @if (job.lightweightResult.legacyIndicators.length) {
                  <ul>
                    @for (indicator of job.lightweightResult.legacyIndicators; track indicator.signal + indicator.filePath) {
                    <li><strong>{{ indicator.signal }}</strong> — {{ indicator.filePath }} ({{ indicator.severity }})</li>
                    }
                  </ul>
                  } @else {
                  <span>—</span>
                  }
                </div>
                <div class="detail-item detail-full">
                  <span class="detail-label">Modern Indicators</span>
                  @if (job.lightweightResult.modernIndicators.length) {
                  <ul>
                    @for (indicator of job.lightweightResult.modernIndicators; track indicator.signal + indicator.filePath) {
                    <li><strong>{{ indicator.signal }}</strong> — {{ indicator.filePath }} ({{ indicator.severity }})</li>
                    }
                  </ul>
                  } @else {
                  <span>—</span>
                  }
                </div>
              </div>
            </mat-card-content>
          </mat-card>
          }

          <!-- Live Logs (shown for running or recent jobs) -->
          <mat-card>
            <mat-card-header>
              <mat-card-title>
                Live Logs
                @if (isRunning(job)) {
                  <mat-spinner diameter="18" style="display: inline-block; margin-left: 10px;"></mat-spinner>
                }
              </mat-card-title>
              <mat-card-subtitle>Assessment log stream from the server</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <div #logConsoleEl class="log-console">
                @if (logLines.length === 0) {
                  <span class="log-placeholder">
                    {{ isRunning(job) || isPending(job) ? 'Waiting for log entries...' : 'No log entries captured for this job.' }}
                  </span>
                }
                @for (line of logLines; track $index) {
                  <div class="log-line">{{ line }}</div>
                }
              </div>
            </mat-card-content>
          </mat-card>
        }

      </div>
    </div>
  </section>
</main>
